import { useQueryClient } from '@tanstack/react-query';
import { Check, CheckCircle2 } from 'lucide-react';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError } from '~/api/errors';
import { queryKeys } from '~/api/queryKeys';
import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { DEFAULT_CATCHUP_WINDOW_SECONDS } from '~/features/catalog-operations/constants/schedules';
import {
  useCreateSchedule,
  useSchedulePreview,
} from '~/features/catalog-operations/hooks/use-catalog-ops-schedules';
import type {
  CadenceDraft,
  OverlapPolicy,
  ScheduleSummary,
} from '~/features/catalog-operations/types/catalog-ops-schedule.types';
import {
  cadenceDraftToSpec,
  defaultCadenceDraft,
  validateCadence,
} from '~/features/catalog-operations/utils/schedule-cadence';
import { cn } from '~/utils/cn';
import { formatInZone } from '~/utils/date';

import { ScheduleCadenceForm } from './schedule-cadence-form';
import { ScheduleJobPicker } from './schedule-job-picker';
import { ScheduleReviewStep } from './schedule-review-step';

/** How long the cadence form settles before the fire-time preview is refetched.
 *  Fire times come from the server (it owns cron parsing and DST), so this is a
 *  real request — one per pause in typing, not one per keystroke. */
const PREVIEW_DEBOUNCE_MS = 450;

type WizardStep = 1 | 2 | 3;

/** Which control a failure belongs against. Every documented status maps to a
 *  different message on a different step — a generic "save failed" toast would
 *  leave the user with no idea which field to change. */
interface WizardError {
  step: WizardStep;
  title: string;
  body: string;
  tone: 'error' | 'warning';
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Numbered stepper — the steps are a real, ordered sequence, so they read as
 *  1-2-3 with connectors. Completed steps get a check and are clickable to jump
 *  back (going back needs no validation); the current step is ringed; upcoming
 *  steps stay muted and inert. */
function StepMeter({
  step,
  onStepClick,
  locked = false,
}: {
  step: WizardStep;
  onStepClick: (s: WizardStep) => void;
  /** Freezes step navigation — set while a submit is in flight. */
  locked?: boolean;
}) {
  const { t } = useTranslation('catalog-operations');
  const steps = [
    { n: 1 as const, label: t('schedules.wizard.step1') },
    { n: 2 as const, label: t('schedules.wizard.step2') },
    { n: 3 as const, label: t('schedules.wizard.step3') },
  ];
  return (
    <nav
      data-testid="schedules-wizard-stepper"
      aria-label={t('schedules.wizard.stepsAria')}
      className="border-b pb-4"
    >
      <ol className="flex items-center">
        {steps.map(({ n, label }, index) => {
          const state = n < step ? 'complete' : n === step ? 'current' : 'upcoming';
          const clickable = n < step && !locked;
          const isLast = index === steps.length - 1;
          return (
            <Fragment key={n}>
              <li>
                <button
                  type="button"
                  data-testid={`schedules-wizard-step-${String(n)}`}
                  disabled={!clickable}
                  aria-current={state === 'current' ? 'step' : undefined}
                  onClick={() => {
                    if (clickable) onStepClick(n);
                  }}
                  className={cn(
                    'flex items-center gap-2.5',
                    clickable ? 'cursor-pointer' : 'cursor-default',
                  )}
                >
                  <span
                    className={cn(
                      'flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-colors',
                      state === 'complete' && 'bg-primary text-primary-foreground',
                      state === 'current' && 'text-primary ring-primary bg-white ring-2',
                      state === 'upcoming' && 'bg-muted text-muted-foreground',
                    )}
                  >
                    {state === 'complete' ? <Check className="size-4" aria-hidden="true" /> : n}
                  </span>
                  <span
                    className={cn(
                      'text-sm font-medium whitespace-nowrap',
                      state === 'current'
                        ? 'text-primary'
                        : state === 'complete'
                          ? 'text-foreground'
                          : 'text-muted-foreground',
                    )}
                  >
                    {label}
                  </span>
                </button>
              </li>
              {!isLast && (
                <li aria-hidden="true" className="mx-3 h-px flex-1">
                  <span
                    className={cn(
                      'block h-full rounded-full transition-colors',
                      n < step ? 'bg-primary' : 'bg-border',
                    )}
                  />
                </li>
              )}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}

/**
 * "Set Up Schedule" — the three-step create flow: pick a batch, set a cadence,
 * review and create.
 *
 * The wizard leans on `POST /preview` throughout: it validates the `jobId`,
 * returns the config snapshot to display, flags a duplicate before the user
 * submits, and supplies the fire times. Nothing about the cadence is computed
 * client-side beyond form validation.
 */
export function ScheduleWizardDialog({ open, onOpenChange }: Props) {
  const { t } = useTranslation('catalog-operations');
  const queryClient = useQueryClient();

  const [step, setStep] = useState<WizardStep>(1);
  const [jobId, setJobId] = useState<string | null>(null);
  const [draft, setDraft] = useState<CadenceDraft>(defaultCadenceDraft);
  const [name, setName] = useState('');
  const [overlapPolicy, setOverlapPolicy] = useState<OverlapPolicy>('SKIP');
  const [catchupWindowSeconds, setCatchupWindowSeconds] = useState(DEFAULT_CATCHUP_WINDOW_SECONDS);
  const [error, setError] = useState<WizardError | null>(null);
  const [created, setCreated] = useState<ScheduleSummary | null>(null);

  const preview = useSchedulePreview();
  const create = useCreateSchedule();

  const cadenceInvalid = validateCadence(draft) !== null;
  const previewData = preview.data;

  /** Serialized spec — a stable dependency for the preview effect, so it only
   *  refires when the resulting request would actually differ. */
  const specKey = useMemo(() => JSON.stringify(cadenceDraftToSpec(draft)), [draft]);

  /** Maps a failed call onto the step and copy that failure belongs to. */
  function toWizardError(err: unknown): WizardError {
    const status = err instanceof ApiError ? err.statusCode : null;
    switch (status) {
      case 400:
        return {
          step: 2,
          title: t('schedules.errors.400.title'),
          body: t('schedules.errors.400.body'),
          tone: 'error',
        };
      case 404:
        return {
          step: 1,
          title: t('schedules.errors.404.title'),
          body: t('schedules.errors.404.body'),
          tone: 'error',
        };
      case 409:
        return {
          step: 3,
          title: t('schedules.errors.409.title'),
          body: t('schedules.errors.409.body'),
          tone: 'warning',
        };
      case 422:
        return {
          step: 1,
          title: t('schedules.errors.422.title'),
          body: t('schedules.errors.422.body'),
          tone: 'warning',
        };
      default:
        return {
          step,
          title: t('schedules.errors.generic.title'),
          body: err instanceof Error ? err.message : t('schedules.errors.generic.body'),
          tone: 'error',
        };
    }
  }

  function handleFailure(err: unknown) {
    const wizardError = toWizardError(err);
    setError(wizardError);
    setStep(wizardError.step);
    // A 404 means the picker is showing a batch that no longer exists, so the
    // listing is refetched rather than left stale for the next attempt.
    if (err instanceof ApiError && err.statusCode === 404) {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.catalogOperations.batches({}),
        exact: false,
      });
    }
  }

  // Refresh the preview whenever the cadence settles on steps 2 and 3. Invalid
  // drafts are skipped: the server would just 400 on a spec the form already
  // knows is wrong.
  useEffect(() => {
    if (!open || !jobId || step === 1 || cadenceInvalid) return;
    const timer = setTimeout(() => {
      preview.mutate(
        { jobId, spec: cadenceDraftToSpec(draft) },
        {
          onSuccess: () => {
            setError(null);
          },
          onError: handleFailure,
        },
      );
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
    // `specKey` stands in for `draft` — it changes only when the request would.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, jobId, step, cadenceInvalid, specKey]);

  function reset() {
    setStep(1);
    setJobId(null);
    setDraft(defaultCadenceDraft());
    setName('');
    setOverlapPolicy('SKIP');
    setCatchupWindowSeconds(DEFAULT_CATCHUP_WINDOW_SECONDS);
    setError(null);
    setCreated(null);
    preview.reset();
    create.reset();
  }

  function close() {
    onOpenChange(false);
    // Deferred so the closing animation doesn't play against a blank dialog.
    setTimeout(reset, 200);
  }

  /** Step 1 → 2 goes through preview without a spec: it is the cheapest way to
   *  validate the job and fetch its config before asking for a cadence. */
  function handleContinueFromJob() {
    if (!jobId) return;
    preview.mutate(
      { jobId },
      {
        onSuccess: () => {
          setError(null);
          setStep(2);
        },
        onError: handleFailure,
      },
    );
  }

  function handleCreate() {
    if (!jobId) return;
    const trimmedName = name.trim();
    create.mutate(
      {
        jobId,
        ...(trimmedName ? { name: trimmedName } : {}),
        scheduleSpec: cadenceDraftToSpec(draft),
        overlapPolicy,
        catchupWindowSeconds,
      },
      {
        onSuccess: (schedule) => {
          setCreated(schedule);
          setError(null);
        },
        onError: handleFailure,
      },
    );
  }

  const banner =
    error?.step === step ? (
      <div
        data-testid="schedules-wizard-error-banner"
        className={cn(
          'rounded-lg border border-l-[3px] px-3 py-2.5 text-sm',
          error.tone === 'error'
            ? 'border-error bg-error-50 text-foreground'
            : 'border-warning bg-warning-50 text-foreground',
        )}
      >
        <p data-testid="schedules-wizard-error-banner-title" className="font-semibold">
          {error.title}
        </p>
        <p
          data-testid="schedules-wizard-error-banner-body"
          className="text-muted-foreground mt-0.5"
        >
          {error.body}
        </p>
      </div>
    ) : undefined;

  // Preview flags a duplicate config before submission. Create would 409, and
  // changing the cadence would not help — the fingerprint covers config only —
  // so the action is blocked here rather than after a failed round trip.
  const duplicateBlocked = previewData?.duplicate === true;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !create.isPending) close();
      }}
    >
      <DialogContent
        data-testid="schedules-wizard-dialog"
        // Fixed height (not content-sized) so switching steps or the preview
        // resolving doesn't resize a vertically-centred dialog and make it jump.
        // The done screen is short, so it drops the fixed height.
        className={cn(
          'flex max-h-[92vh] max-w-3xl flex-col gap-4 overflow-hidden',
          !created && 'h-[40rem]',
        )}
        // Can't bail out mid-create — the ✕ joins the footer buttons in being
        // disabled until the call settles.
        closeDisabled={create.isPending}
        onInteractOutside={(e) => {
          if (create.isPending) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (create.isPending) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle
            data-testid="schedules-wizard-dialog-title"
            className="text-lg font-semibold"
          >
            {created
              ? t('schedules.wizard.doneTitle')
              : step === 1
                ? t('schedules.wizard.title')
                : step === 2
                  ? t('schedules.wizard.cadenceTitle')
                  : t('schedules.wizard.reviewTitle')}
          </DialogTitle>
          <DialogDescription>
            {created
              ? t('schedules.wizard.doneSubtitle')
              : step === 1
                ? t('schedules.wizard.subtitle')
                : step === 2
                  ? t('schedules.wizard.cadenceSubtitle')
                  : t('schedules.wizard.reviewSubtitle')}
          </DialogDescription>
        </DialogHeader>

        {!created && (
          <StepMeter
            step={step}
            locked={create.isPending}
            onStepClick={(s) => {
              setStep(s);
              setError(null);
            }}
          />
        )}

        {/* px, not just pr: `overflow-y-auto` clips on the x-axis too, so a
            full-width input's 2px focus ring is cut off at the left edge without
            a little horizontal breathing room. */}
        <div className="min-h-0 flex-1 overflow-y-auto px-1.5">
          {created ? (
            <div data-testid="schedules-wizard-done" className="py-2 text-center">
              <span className="bg-success-50 text-success mx-auto mb-3 flex size-12 items-center justify-center rounded-full">
                <CheckCircle2 className="size-6" aria-hidden="true" />
              </span>
              <p data-testid="schedules-wizard-done-heading" className="text-base font-semibold">
                {t('schedules.wizard.doneHeading', { name: created.name })}
              </p>
              <p
                data-testid="schedules-wizard-done-first-run"
                className="text-muted-foreground mx-auto mt-1 max-w-md text-sm"
              >
                {created.nextRunAt
                  ? t('schedules.wizard.doneFirstRun', {
                      when: formatInZone(created.nextRunAt, draft.timezone),
                      zone: draft.timezone.replace('_', ' '),
                    })
                  : t('schedules.wizard.doneNoTimes')}
              </p>
              <div className="mt-4 rounded-xl border p-4 text-left">
                <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
                  <dt className="text-muted-foreground">{t('schedules.review.cadence')}</dt>
                  <dd data-testid="schedules-wizard-done-cadence" className="font-medium">
                    {created.cadenceLabel}
                  </dd>
                  <dt className="text-muted-foreground">{t('schedules.review.overlapShort')}</dt>
                  <dd data-testid="schedules-wizard-done-overlap" className="font-medium">
                    {t(`schedules.overlap.${overlapPolicy}.title`)}
                  </dd>
                </dl>
              </div>
              {/* Write-side responses carry no history, so the list is where real
                  numbers come from. Said plainly rather than showing zeros. */}
              <div className="border-success bg-success-50 text-muted-foreground mt-3 rounded-lg border border-l-[3px] px-3 py-2.5 text-left text-sm">
                {t('schedules.wizard.doneStatsNote')}
              </div>
              {/* The only thing left to do is dismiss, so the action sits centred
                  right under the message as the primary button instead of in the
                  footer's back/next slot. */}
              <div className="mt-5 flex justify-center">
                <Button
                  data-testid="schedules-wizard-done-button"
                  className="cursor-pointer"
                  onClick={close}
                >
                  {t('schedules.wizard.done')}
                </Button>
              </div>
            </div>
          ) : step === 1 ? (
            <ScheduleJobPicker
              testId="schedules-job-picker"
              selectedJobId={jobId}
              onSelect={(id) => {
                setJobId(id);
                setError(null);
              }}
              banner={banner}
            />
          ) : step === 2 ? (
            <div data-testid="schedules-wizard-cadence-step" className="space-y-4">
              {banner}
              <ScheduleCadenceForm
                testId="schedules-cadence"
                draft={draft}
                onChange={setDraft}
                nextRuns={previewData?.nextRunPreview ?? []}
                isPreviewLoading={preview.isPending}
                serverError={error?.step === 2 ? error.body : null}
              />
            </div>
          ) : (
            <ScheduleReviewStep
              draft={draft}
              preview={previewData}
              name={name}
              onNameChange={setName}
              overlapPolicy={overlapPolicy}
              onOverlapChange={setOverlapPolicy}
              catchupWindowSeconds={catchupWindowSeconds}
              onCatchupChange={setCatchupWindowSeconds}
              banner={
                banner ??
                (duplicateBlocked ? (
                  <div
                    data-testid="schedules-wizard-duplicate-banner"
                    className="border-warning bg-warning-50 rounded-lg border border-l-[3px] px-3 py-2.5 text-sm"
                  >
                    <p className="font-semibold">{t('schedules.errors.duplicate.title')}</p>
                    <p className="text-muted-foreground mt-0.5">
                      {t('schedules.errors.duplicate.body')}
                    </p>
                  </div>
                ) : undefined)
              }
            />
          )}
        </div>

        {/* No footer on the done screen — its single action is the centred
            primary button under the message above. */}
        {!created && (
          <DialogFooter className="gap-2 border-t pt-4 sm:justify-between">
            <Button
              data-testid={
                step === 1 ? 'schedules-wizard-cancel-button' : 'schedules-wizard-back-button'
              }
              variant="outline"
              className="cursor-pointer"
              disabled={create.isPending}
              onClick={() => {
                if (step === 1) close();
                else setStep((step - 1) as WizardStep);
              }}
            >
              {step === 1 ? t('schedules.wizard.cancel') : t('schedules.wizard.back')}
            </Button>
            {step === 3 ? (
              <Button
                data-testid="schedules-wizard-create-button"
                className="cursor-pointer"
                isLoading={create.isPending}
                disabled={duplicateBlocked || cadenceInvalid}
                onClick={handleCreate}
              >
                {t('schedules.wizard.create')}
              </Button>
            ) : (
              <Button
                data-testid="schedules-wizard-continue-button"
                className="cursor-pointer"
                isLoading={step === 1 && preview.isPending}
                disabled={step === 1 ? !jobId : cadenceInvalid}
                onClick={() => {
                  if (step === 1) handleContinueFromJob();
                  else setStep(3);
                }}
              >
                {t('schedules.wizard.continue')}
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
